import { type FC, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import type { HostOperatorEntity } from "src/app/features/Acquirer/types/subTypes/HostOperatorEntity";
import type { ExtendedColumnsType } from "../../../index";
import { PropertyTable } from "../../../Table/components/PropertyTable";
import { TableAddNewLineButton } from "../../../Table/components/PropertyTable/TableAddNewLineButton";
import { Actions } from "../../../Table/types/types";
import { type AntCustomFormField, type CommonFieldAttributes } from "../../types/types";
import { Checkbox } from "../Checkbox/Checkbox";
import { NumberInput } from "../Number/NumberInput";
import { TextInput } from "../Text/TextInput";
import { LogoUploadButton } from "./LogoUploadButton";

export interface AcquirersOperatorsTableProps extends AntCustomFormField<HostOperatorEntity[]>, CommonFieldAttributes {}

export const AcquirersOperatorsTable: FC<AcquirersOperatorsTableProps> = ({ value = [], onChange, readOnly }) => {
    // Assign a unique rowId to each row (persisted or new)
    const assignRowIds = (array: HostOperatorEntity[]) =>
        array.map((item) => ({
            ...item,
            rowId: item.rowId ?? item.id ?? uuidv4(),
        }));

    const appendRowId = (array: HostOperatorEntity[]) =>
        assignRowIds(
            array.slice().sort((a, b) => {
                if (a.id === undefined && b.id !== undefined) return 1;
                if (a.id !== undefined && b.id === undefined) return -1;
                return Number(a.id ?? 0) - Number(b.id ?? 0);
            }),
        );

    // Use rowId for all row operations
    const onInput = (rowId: string | number, key: keyof HostOperatorEntity, newValue: string | boolean) => {
        const newData = value.map((row) => {
            if (row.rowId !== rowId) return row;
            return { ...row, [key]: newValue };
        });
        onDataChanged(newData);
    };

    const onLogoChange = (rowId: string | number, base64: string, fileName: string) => {
        const newData = value.map((row) => (row.id === rowId ? { ...row, logoFile: base64, logoName: fileName } : row));
        onDataChanged(newData);
    };

    const onLogoRemove = (rowId: string | number) => {
        const newData = value.map((row) => (row.id === rowId ? { ...row, logoFile: undefined, logoName: undefined } : row));
        onDataChanged(newData);
    };

    const getColumnsConfig = (t: Function): ExtendedColumnsType<HostOperatorEntity>[] => {
        return [
            {
                title: t("acquirerCreateForm.table.systemId"),
                key: "id",
                dataIndex: "id",
            },
            {
                title: t("acquirerCreateForm.table.operatorId"),
                key: "operatorId",
                dataIndex: "operatorId",
                render: (_, record) => (
                    <NumberInput
                        name={`operatorId-${record.rowId}`}
                        label=""
                        value={record.operatorId?.toString()}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.rowId!, "operatorId", newValue)}
                    />
                ),
            },
            {
                title: t("acquirerCreateForm.table.name"),
                key: "name",
                dataIndex: "name",
                render: (_, record) => (
                    <TextInput
                        name={`name-${record.rowId}`}
                        label=""
                        value={record.name}
                        readOnly={false}
                        onChange={(newValue) => onInput(record.rowId!, "name", newValue)}
                    />
                ),
            },
            {
                title: t("acquirerCreateForm.table.logo"),
                key: "logoName",
                dataIndex: "logoName",
                render: (_, record) => (
                    <LogoUploadButton
                        value={record.logoName}
                        readOnly={false}
                        onChange={(base64, fileName) => onLogoChange(record.rowId!, base64, fileName)}
                        onRemove={() => onLogoRemove(record.rowId!)}
                    />
                ),
            },
            {
                title: t("acquirerCreateForm.table.active"),
                key: "status",
                dataIndex: "status",
                render: (_, record) => (
                    <Checkbox
                        name={`status-${record.rowId}`}
                        label=""
                        value={record.status ?? false}
                        readOnly={false}
                        onChange={(checked) => onInput(record.rowId!, "status", checked)}
                    />
                ),
            },
        ];
    };

    const onDataChanged = useCallback(
        (data: HostOperatorEntity[]) => {
            if (onChange) {
                onChange(assignRowIds(data));
            }
        },
        [onChange],
    );

    const addNewLine = () => {
        const emptyTableData = { id: undefined, operatorId: undefined, name: "", logo: "", status: false, rowId: uuidv4() };
        const newTableValue = [...assignRowIds(value), emptyTableData];
        onDataChanged(newTableValue);
    };

    return (
        <PropertyTable<HostOperatorEntity>
            name="acquirerOperators_table"
            getColumnsConfig={getColumnsConfig}
            propertyAsRowKey="rowId"
            tableData={appendRowId(value)}
            readOnly={readOnly || false}
            onDataChanged={onDataChanged}
            actions={[Actions.DELETE]}
            customActions={[<TableAddNewLineButton key="hostOperators_add-new-line" callback={addNewLine} />]}
        />
    );
};
